import { ValidSizingCalculatorAttributesType } from 'data/types';
import { DefaultCategoryType } from 'features/data/data-slice';

export type FieldConfig = {
  key: string;
  label: string;
  subLabel?: string;
  type: 'select' | 'number' | 'boolean';
  options?: string[] | 'fromValidAttributes';
  attributeKey?: string;
  min?: number;
  suffix?: string;
  conditionalRender?: (values: Record<string, unknown>) => boolean;
};

export type SizingItemConfig = {
  id: string;
  title: string;
  defaultsKey: string;
  validAttributeKey: keyof ValidSizingCalculatorAttributesType;
  category: DefaultCategoryType;
  fields: FieldConfig[];
  getMockItem: (
    currentValues: Record<string, unknown>
  ) => Record<string, unknown>;
};

export const verticalGlassConfig: SizingItemConfig = {
  id: 'verticalGlass',
  title: 'Windows',
  defaultsKey: 'verticalGlass',
  validAttributeKey: 'verticalGlass',
  category: 'fenestration',
  fields: [
    {
      key: 'panes',
      label: 'Panes',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'panes'
    },
    {
      key: 'constructionType',
      label: 'Construction Type',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'constructionType'
    },
    {
      key: 'sash',
      label: 'Sash Type',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'sash'
    },
    {
      key: 'bugScreen',
      label: 'Bug Screen',
      type: 'select',
      options: ['Full Indoor', 'None']
    },
    {
      key: 'roofOverhangFeet',
      label: 'Roof Overhang',
      subLabel: '(ft)',
      type: 'number',
      min: 1,
      suffix: ' ft'
    },
    {
      key: 'distanceTopOfWindowToRoofOverhang',
      label: 'Top of Window to Roof',
      subLabel: '(ft)',
      type: 'number',
      min: 1,
      suffix: ' ft'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    width: 4,
    height: 4
  })
};

export const solidDoorConfig: SizingItemConfig = {
  id: 'solidDoor',
  title: 'Solid Doors',
  defaultsKey: 'solidDoor',
  validAttributeKey: 'solidDoors',
  category: 'fenestration',
  fields: [
    {
      key: 'material',
      label: 'Material',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'material'
    },
    {
      key: 'stormMaterial',
      label: 'Storm Material',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'stormMaterial'
    },
    {
      key: 'core',
      label: 'Core',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'core'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    width: 3,
    height: 7
  })
};

export const skylightConfig: SizingItemConfig = {
  id: 'skylight',
  title: 'Skylights',
  defaultsKey: 'skylight',
  validAttributeKey: 'skylights',
  category: 'fenestration',
  fields: [
    {
      key: 'panes',
      label: 'Panes',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'panes'
    },
    {
      key: 'sash',
      label: 'Sash',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'sash'
    },
    {
      key: 'constructionType',
      label: 'Construction Type',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'constructionType'
    },
    {
      key: 'glazingPanelInclination',
      label: 'Glazing Panel Inclination',
      type: 'select',
      options: [
        '30 Degrees From Horizontal',
        '45 Degrees From Horizontal',
        '60 Degrees From Horizontal',
        'Horizontal'
      ]
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    length: 3,
    width: 3,
    exposure: 'Horizontal'
  })
};

export const aboveGradeWallConfig: SizingItemConfig = {
  id: 'aboveGradeWall',
  title: 'Above Grade Walls',
  defaultsKey: 'aboveGradeWall',
  validAttributeKey: 'aboveGradeWalls',
  category: 'walls',
  fields: [
    {
      key: 'isShaded',
      label: 'Shaded Wall',
      type: 'boolean'
    },
    {
      key: 'wallType',
      label: 'Wall Type',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'wallType'
    },
    {
      key: 'cavityInsulation',
      label: 'Cavity Insulation',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'cavityInsulation',
      conditionalRender: (values) => values.wallType !== 'Block'
    },
    {
      key: 'boardInsulation',
      label: 'Board Insulation',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'boardInsulation'
    },
    {
      key: 'exteriorFinish',
      label: 'Exterior Finish',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'exteriorFinish'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    width: 10,
    height: 8,
    exposure: 'North',
    verticalGlass: [],
    solidDoors: []
  })
};

export const belowGradeWallConfig: SizingItemConfig = {
  id: 'belowGradeWall',
  title: 'Below Grade Walls',
  defaultsKey: 'belowGradeWall',
  validAttributeKey: 'belowGradeWalls',
  category: 'walls',
  fields: [
    {
      key: 'insulationType',
      label: 'Insulation Type',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationType'
    },
    {
      key: 'insulationValue',
      label: 'Insulation Value',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationValue',
      conditionalRender: (values) => values.insulationType !== 'None'
    },
    {
      key: 'insulationCoverage',
      label: 'Insulation Coverage',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationCoverage',
      conditionalRender: (values) => values.insulationType !== 'None'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    width: 10,
    height: 8,
    belowGradeDepth: 6,
    exposure: 'North',
    verticalGlass: [],
    solidDoors: []
  })
};

export const ceilingBelowRoofJoistsConfig: SizingItemConfig = {
  id: 'ceilingBelowRoofJoists',
  title: 'Ceiling Below Roof Joists',
  defaultsKey: 'ceilingBelowRoofJoists',
  validAttributeKey: 'ceilingsBelowRoof',
  category: 'ceilings',
  fields: [
    {
      key: 'insulationValue',
      label: 'Insulation Value',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationValue'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    area: 100,
    ceilingType: 'Below Roof Joists',
    skylights: []
  })
};

export const ceilingOnExposedBeamsConfig: SizingItemConfig = {
  id: 'ceilingOnExposedBeams',
  title: 'Ceiling On Exposed Beams',
  defaultsKey: 'ceilingOnExposedBeams',
  validAttributeKey: 'ceilingsBelowRoof',
  category: 'ceilings',
  fields: [
    {
      key: 'insulationValue',
      label: 'Insulation Value',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationValue'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    area: 100,
    ceilingType: 'On Exposed Beams',
    skylights: []
  })
};

export const ceilingBelowAtticConfig: SizingItemConfig = {
  id: 'ceilingBelowAttic',
  title: 'Ceiling Below Attic',
  defaultsKey: 'ceilingBelowAttic',
  validAttributeKey: 'ceilingsBelowAttic',
  category: 'ceilings',
  fields: [
    {
      key: 'insulationValue',
      label: 'Insulation Value',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationValue'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    area: 100,
    ceilingType: 'Under Attic'
  })
};

export const floorOverUnconditionedSpaceConfig: SizingItemConfig = {
  id: 'floorOverUnconditionedSpace',
  title: 'Floor Over Unconditioned Space',
  defaultsKey: 'floorOverUnconditionedSpace',
  validAttributeKey: 'floorsOverUnconditionedSpace',
  category: 'floors',
  fields: [
    {
      key: 'floorCover',
      label: 'Floor Cover',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'floorCover'
    },
    {
      key: 'insulationValue',
      label: 'Insulation Value',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationValue'
    },
    {
      key: 'exposedWallInsulationValue',
      label: 'Exposed Wall Insulation',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'exposedWallInsulationValue'
    },
    {
      key: 'unconditionedSpaceInfiltrationType',
      label: 'Unconditioned Space Infiltration Type',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'unconditionedSpaceInfiltrationType'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    area: 100
  })
};

export const floorOverExposedSpaceConfig: SizingItemConfig = {
  id: 'floorOverExposedSpace',
  title: 'Floor Over Exposed Space',
  defaultsKey: 'floorOverExposedSpace',
  validAttributeKey: 'floorsOverExposedSpace',
  category: 'floors',
  fields: [
    {
      key: 'floorCover',
      label: 'Floor Cover',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'floorCover'
    },
    {
      key: 'insulationValue',
      label: 'Insulation Value',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationValue'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    area: 100
  })
};

export const slabFloorConfig: SizingItemConfig = {
  id: 'slabFloor',
  title: 'Slab Floor',
  defaultsKey: 'slabFloor',
  validAttributeKey: 'slabFloors',
  category: 'floors',
  fields: [
    {
      key: 'slabInsulationArrangement',
      label: 'Insulation Arrangement',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'slabInsulationArrangement'
    },
    {
      key: 'insulationValue',
      label: 'Insulation Value',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'insulationValue',
      conditionalRender: (values) => values.slabInsulationArrangement !== 'None'
    },
    {
      key: 'soilType',
      label: 'Soil Type',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'soilType'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    exposedEdgeFeet: 40,
    feetBelowGrade: 1
  })
};

export const basementFloorConfig: SizingItemConfig = {
  id: 'basementFloor',
  title: 'Basement Floor',
  defaultsKey: 'basementFloor',
  validAttributeKey: 'basementFloors',
  category: 'floors',
  fields: [
    {
      key: 'shortestSideWidthFeet',
      label: 'Shortest Side Width',
      subLabel: '(ft)',
      type: 'select',
      options: 'fromValidAttributes',
      attributeKey: 'shortestSideWidthFeet'
    }
  ],
  getMockItem: (currentValues) => ({
    ...currentValues,
    uniqueId: 'defaults',
    valid: true,
    area: 100,
    feetBelowGrade: 3,
    shortestSideWidthFeet: 24
  })
};

export type AccordionCategoryConfig = {
  id: DefaultCategoryType;
  title: string;
  description: string;
  items: SizingItemConfig[];
};

export const sizingDefaultsCategories: AccordionCategoryConfig[] = [
  {
    id: 'fenestration',
    title: 'Glass/Door Settings',
    description: 'Windows, doors, and skylights',
    items: [verticalGlassConfig, solidDoorConfig, skylightConfig]
  },
  {
    id: 'walls',
    title: 'Wall Settings',
    description: 'Above and below grade wall construction',
    items: [aboveGradeWallConfig, belowGradeWallConfig]
  },
  {
    id: 'ceilings',
    title: 'Ceiling Settings',
    description: 'Ceiling types and insulation',
    items: [
      ceilingBelowRoofJoistsConfig,
      ceilingOnExposedBeamsConfig,
      ceilingBelowAtticConfig
    ]
  },
  {
    id: 'floors',
    title: 'Floor Settings',
    description: 'Floor types and insulation',
    items: [
      floorOverUnconditionedSpaceConfig,
      floorOverExposedSpaceConfig,
      slabFloorConfig,
      basementFloorConfig
    ]
  }
];
